import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import axios from 'helpers/gastro';
import Cookies from 'cookies-js';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import roles from 'helpers/roles';
import { isGranted } from 'helpers/helpers';
import { fetchZoneCategories } from 'actions/ZoneCategories';
import { fetchProductionTags } from 'actions/Dictionary';
// eslint-disable-next-line import/order
import { fetchSubBrands } from 'actions/SubBrands';

// components
import { SelectInput } from 'components';
import Card from 'components/Card/Card';
import Button from 'components/CustomButtons/Button';
import Archive from 'components/Archive/Archive';
import CardBody from 'components/Card/CardBody';
import GridItem from 'components/Grid/GridItem';
import SelectAll from 'components/SelectAll';
import GridContainer from 'components/Grid/GridContainer';
import AccessDenied from 'components/Security/AccessDenied';
import ReportConfigurator from 'components/Report/ReportConfigurator';
import { AddCircle } from '@material-ui/icons';
import { Dialog, DialogContent, Tooltip } from '@material-ui/core';
import FormLabel from '@material-ui/core/FormLabel';
// eslint-disable-next-line import/order
import CircularProgress from '@material-ui/core/CircularProgress';

// styles
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import Info from '@material-ui/icons/Info';
import CardWrapper from 'components/Card/CardWrapper';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/styles';
import produce from 'immer';
import classNames from 'classnames';
import moment from 'moment';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import ProductionTagSelect, { MODES } from 'components/ProductionTagSelect';
import EXTERNAL_URLS from 'constants/externalUrls';
import DEFAULT_SALES_MARKET_LANG from 'constants/defaultSalesMarketLang';

import MIME_TYPES from '../constants/mimeTypes';
import ReportPreview from '../ReportPreviev/ReportPreview';
import DEFAULT_MULTIPLIER from './constants/defaultMultiplier';
import MIME_TYPES_DATA from '../constants/mimeTypesData';
import DownloadReportButtons from '../DownloadReportButtons/DownloadReportButtons';
import IndividualMultiplierReportRow from './IndividualMultiplierReportRow';
import { DOWNLOAD_REPORT_BUTTON_ID } from './constants/reportButtonId';

const defaultRow = () => ({
  key: uuidv4(),
  brands: [],
  multiplier: DEFAULT_MULTIPLIER,
  subBrands: [],
  dateFrom: '',
  dateTo: '',
  zoneCategories: [],
  includeSubscriptions: false,
  recipeTags: [],
  isIndividualMultipliersEnabled: false,
});

const useStyles = makeStyles({
  ...buttonsStyle,
  ...extendedFormsStyle,
  cardWrapper: {
    marginTop: '20px',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      marginRight: '0.2em',
    },
    '& > svg': {
      margin: '0 0.1em',
    },
  },
  dialogContent: {
    textAlign: 'center',
  },
  formLabel: {
    marginBottom: '2px',
  },
  tooltip: {
    color: 'grey',
    marginLeft: '5px',
    marginBottom: '-4px',
  },
  submitButtons: {
    display: 'flex',
  },
});

const getValidationFlags = ({ dateFrom, dateTo, useDateRange }) => {
  const dateFromMoment = moment(dateFrom, 'YYYY-MM-DD', true);
  const dateToMoment = moment(dateTo, 'YYYY-MM-DD', true);
  const isDateFromValid = dateFromMoment.isValid();
  const isDateToValid = dateToMoment.isValid();

  const isDateFromEmpty = !isDateFromValid;
  const isDateToEmpty = useDateRange && !isDateToValid;
  const isDateRangeInvalid =
    isDateFromValid && isDateToValid && dateFromMoment.isAfter(dateToMoment);

  return {
    dateFromMoment,
    dateToMoment,
    isDateFromValid,
    isDateToValid,
    isDateFromEmpty,
    isDateToEmpty,
    isDateRangeInvalid,
  };
};

const transformMatrixData = data => {
  const result = [];

  const pushEntry = (item, entry) => {
    if (item.dietIri === 'shopDishes') {
      entry.shopDishes = {
        multiplier: parseFloat(item.multiplier),
      };
    } else {
      entry.diets.push({
        diet: item.dietIri,
        calorific: item.calorificIri,
        multiplier: parseFloat(item.multiplier),
      });
    }
  };

  data.forEach(item => {
    const brandEntry = result.find(entry => entry.brand === item.brandIri);

    if (Number(item.multiplier) === 1) {
      return;
    }

    if (brandEntry) {
      pushEntry(item, brandEntry);
    } else {
      const newBrandEntry = {
        brand: item.brandIri,
        diets: [],
      };

      pushEntry(item, newBrandEntry);
      result.push(newBrandEntry);
    }
  });

  return result;
};

const IndividualMultiplierReport = ({
  name,
  title,
  url,
  previewXlsxAvailable,
  mimeTypes,
  role,
  reportConfiguration,
  fileName,
  useDateRange,
  useZoneCategories,
  showMultiplier,
  usePost,
  useRecipeTags,
  singleSelectRecipeTags,
  useWarehouse,
  singleSelectWarehouse,
  customActions,
  archiveReportNames,
  isSalesMarketLangViewEnabled = false,
  additionalParams,
  labelSelectTags,
  children,
  useSubBrands,
  isTagSelectV2Enabled,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);

  const brandId = useSelector(state => state.Brands.brand.id);
  const { brands, company, zoneCategories } = useSelector(state => ({
    ...state.Auth.user,
    ...state.ZoneCategories,
  }));
  const subBrandsOptions = useSelector(state => state.SubBrands.subBrands);
  const recipeTagsOptions = useSelector(
    state => state.Dictionary.productionTags
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [excelContent, setExcelContent] = useState({});
  const [isReportPreviewOpen, setIsReportPreviewOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [parameters, setParameters] = useState([defaultRow()]);
  const [multiplierMatrixDataRows, setMultiplierMatrixDataRows] = useState({});

  // TODO: delete after the SelectAll component is removed
  const [recipeTags, setRecipeTags] = useState([]);
  // TODO: rename to recipeTags after the SelectAll component is removed
  const [recipeTagsV2, setRecipeTagsV2] = useState([]);
  const [recipeTagsMode, setRecipeTagsMode] = useState(MODES.or);
  const [warehouse, setWarehouse] = useState('');
  const [availableWarehouses, setAvailableWarehouses] = useState([]);

  const classes = useStyles();

  useEffect(() => {
    (async () => {
      const brandsIris = brands.map(brand => brand['@id']);
      if (useZoneCategories && isGranted(roles.ROLE_SHOW_ZONE_CATEGORY)) {
        dispatch(fetchZoneCategories(brandsIris));
      }
      if (useRecipeTags) {
        dispatch(fetchProductionTags());
      }
      if (useWarehouse) {
        try {
          const newAvailableWarehouses = await axios.get(
            '/fakturownia/warehouses'
          );

          if (Array.isArray(newAvailableWarehouses?.data)) {
            setWarehouse(
              isEmpty(newAvailableWarehouses?.data)
                ? null
                : newAvailableWarehouses?.data[0]
            );
            setAvailableWarehouses(newAvailableWarehouses?.data);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(e);
        }
      }

      dispatch(fetchSubBrands(brandsIris));

      const cookie = JSON.parse(
        Cookies.get('individual_multipliers_report') || '{}'
      );

      const isCookieStateValid =
        cookie.parameters &&
        cookie.recipeTags &&
        cookie.warehouse &&
        cookie.availableWarehouses;

      if (isCookieStateValid) {
        setParameters(cookie.parameters);
        setRecipeTags(cookie.recipeTags);
        setWarehouse(cookie.warehouse);
        setAvailableWarehouses(cookie.availableWarehouses);
      }

      setIsInitialized(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useWarehouse]);

  const addNextRow = () => {
    setParameters(prevState => [...prevState, defaultRow()]);
  };

  const onRowRemoved = key => {
    setParameters(prevState =>
      prevState.filter(parameter => parameter.key !== key)
    );
  };

  const onRowUpdated = row => {
    const rowIndex = parameters.findIndex(
      parameter => parameter.key === row.key
    );

    setParameters(
      produce(draft => {
        draft[rowIndex] = { ...draft[rowIndex], ...row };
      })
    );
  };

  const getToastError = ({ emptyDate, rangeIsInvalid }) => {
    if (rangeIsInvalid) {
      return t('report.individualMultiplier.deliveryToDateAfterFromDate');
    }

    if (emptyDate) {
      return t('report.individualMultiplier.fillDeliveryDates');
    }

    return '';
  };

  const showToastOnError = () => {
    const rowErrors = [];

    parameters.forEach(({ dateFrom, dateTo }) => {
      const { isDateFromEmpty, isDateToEmpty, isDateRangeInvalid } =
        getValidationFlags({ dateFrom, dateTo, useDateRange });

      const dateErrors = {};

      if (isDateRangeInvalid) {
        dateErrors.rangeIsInvalid = true;
      }

      if (isDateFromEmpty || isDateToEmpty) {
        dateErrors.emptyDate = true;
      }

      if (Object.keys(dateErrors).length > 0) {
        rowErrors.push(dateErrors);
      }
    });

    const result = rowErrors.reduce(
      (acc, curr) =>
        Object.entries(curr).reduce((innerAcc, [key, value]) => {
          if (value) {
            return {
              ...innerAcc,
              [key]: true,
            };
          }
          return innerAcc;
        }, acc),
      {}
    );

    const error = getToastError(result);

    if (error) {
      toast.error(error);
    }
  };

  const getErrors = () =>
    parameters.map(({ brands: paramBrands, dateFrom, dateTo }) => {
      const errorMessages = {};

      const {
        isBrandsEmpty,
        isDateFromEmpty,
        isDateToEmpty,
        isDateRangeInvalid,
      } = getValidationFlags({
        brands: paramBrands,
        dateFrom,
        dateTo,
        useDateRange,
      });

      if (isBrandsEmpty) {
        errorMessages.brands = t('report.individualMultiplier.requiredField');
      }

      if (isDateFromEmpty) {
        errorMessages.dateFrom = t('report.individualMultiplier.requiredField');
      }

      if (isDateToEmpty) {
        errorMessages.dateTo = t('report.individualMultiplier.requiredField');
      }

      if (isDateRangeInvalid) {
        errorMessages.dateFrom = t(
          'report.individualMultiplier.fromDateBeforeToDate'
        );
        errorMessages.dateTo = t(
          'report.individualMultiplier.toDateAfterFromDate'
        );
      }

      return errorMessages;
    });

  const handleWarehouse = (ev, selectedValue) => {
    setWarehouse(selectedValue);
  };

  const handleRecipeTags = selected => {
    setRecipeTags(selected.filter(el => typeof el !== 'undefined'));
  };

  const handleUpdateRecipeTagsV2AndMode = (
    newRecipeTagsMode,
    newRecipeTags
  ) => {
    setRecipeTagsMode(newRecipeTagsMode);
    setRecipeTagsV2(newRecipeTags);
  };

  const filterRecipeTags = tags =>
    tags.filter(val => val).map(val => val.value);

  const handlePreviewGenerate = (mimeType, lang) => {
    setIsGeneratingPreview(true);

    const validationErrors = getErrors();
    setErrors(validationErrors);

    const isValid = validationErrors.every(
      err => Object.keys(err).length === 0
    );

    if (!isValid) {
      showToastOnError();
      setIsGeneratingPreview(false);
      return;
    }

    let params;

    try {
      // eslint-disable-next-line no-use-before-define
      params = getParams();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error(t('status.error.somethingWentWrong'));
      setIsGeneratingPreview(false);

      return;
    }

    const actionsConfig = {
      responseType: 'blob',
      headers: { accept: MIME_TYPES.json },
      forcedLanguage: lang,
    };

    axios
      .post(url, params, actionsConfig)
      // eslint-disable-next-line promise/prefer-await-to-then
      .then(
        async response => {
          if (response.data) {
            const text = await response.data.text();
            const parsedExcelContent = JSON.parse(text);

            setExcelContent(parsedExcelContent);
            setIsGeneratingPreview(false);
            setIsReportPreviewOpen(true);
          } else {
            toast.error(t('reports.noAccess'));
          }
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks
        error => {
          if (typeof error.response === 'undefined') {
            toast.error(`${t('reports.cannotGeneratePreview')} ${error}`);
          } else {
            toast.error(
              `${t('reports.cannotGeneratePreview')} ${
                error.response.data['hydra:description']
              }`
            );
          }
          setIsGeneratingPreview(false);
        }
      )
      // eslint-disable-next-line promise/prefer-await-to-then
      .finally(() => {
        setIsGeneratingPreview(false);
      });
  };

  const handleGenerate = (mimeType, lang) => {
    setIsGenerating(true);
    const validationErrors = getErrors();
    setErrors(validationErrors);

    const isValid = validationErrors.every(
      err => Object.keys(err).length === 0
    );

    if (!isValid) {
      showToastOnError();
      setIsGenerating(false);
      return;
    }

    let params;

    try {
      Cookies.set(
        'individual_multipliers_report',
        JSON.stringify({
          parameters,
          recipeTags,
          warehouse,
          availableWarehouses,
        })
      );

      // eslint-disable-next-line no-use-before-define
      params = getParams();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error(t('status.error.somethingWentWrong'));
      setIsGenerating(false);
      return;
    }

    const actionsConfig = {
      responseType: 'blob',
      headers: { accept: mimeType },
      forcedLanguage: lang,
    };

    const action = usePost
      ? axios.post(url, params, actionsConfig)
      : axios.get(url, {
          ...actionsConfig,
          params,
        });

    action
      // eslint-disable-next-line promise/prefer-await-to-then
      .then(
        response => {
          if (response.data) {
            const blobUrl = window.URL.createObjectURL(
              new Blob([response.data])
            );

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute(
              'download',
              `${fileName({ parameters })}.${
                MIME_TYPES_DATA[mimeType].extension
              }`
            );

            document.body.appendChild(link);
            link.click();
            setIsGenerating(false);
          } else {
            alert(t('reports.noAccess'));
          }
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks
        error => {
          if (typeof error.response === 'undefined') {
            toast.error(`${t('reports.cannotGenerate')} ${error}`);
          } else {
            toast.error(
              `${t('reports.cannotGenerate')} ${
                error.response.data['hydra:description']
              }`
            );
          }
          setIsGenerating(false);
        }
      )
      // eslint-disable-next-line promise/prefer-await-to-then
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const getParams = () => {
    const mappedParameters = parameters.map(parameter => {
      let mappedRecipeTags = [];

      if (useRecipeTags) {
        mappedRecipeTags = filterRecipeTags(
          singleSelectRecipeTags ? mappedRecipeTags : parameter.recipeTags
        );
      }

      const hasMultiplierMatrixData =
        multiplierMatrixDataRows[parameter.key]?.length > 0;

      const mappedParameter = {
        ...parameter,
        brands: parameter.brands.filter(val => val).map(val => val.value),
        subBrands: parameter.subBrands.filter(val => val).map(val => val.value),
        ...(useZoneCategories && {
          zoneCategories: parameter.zoneCategories
            .filter(val => val)
            .map(val => val.value),
        }),
        recipeTags: mappedRecipeTags,
      };

      if (hasMultiplierMatrixData) {
        delete mappedParameter.multiplier;
        mappedParameter.multiplierMatrix = transformMatrixData(
          multiplierMatrixDataRows[parameter.key]
        );
      } else {
        delete mappedParameter.multiplierMatrix;
        mappedParameter.multiplier = parameter.multiplier;
      }

      return mappedParameter;
    });

    let resultAdditionalParams = {};

    if (typeof additionalParams === 'object') {
      resultAdditionalParams = additionalParams;
    }

    if (useRecipeTags && singleSelectRecipeTags) {
      resultAdditionalParams.recipeTags = isTagSelectV2Enabled
        ? recipeTagsV2
        : filterRecipeTags(recipeTags);

      if (isTagSelectV2Enabled) {
        resultAdditionalParams.recipeTagsOperator = recipeTagsMode;
      }
    }

    if (useWarehouse && singleSelectWarehouse) {
      resultAdditionalParams.warehouse = warehouse;
    }

    return {
      parameters: mappedParameters,
      ...resultAdditionalParams,
    };
  };

  const companyId = parseInt(company.split('/').pop(), 10);
  const reportKey = reportConfiguration?.report || false;
  const fields = reportConfiguration?.fields;

  if (!isInitialized) {
    return null;
  }

  return isGranted(role) ? (
    <>
      <CardWrapper
        title={
          <div className={classes.cardTitle}>
            <span>{title}</span>
            <InfoWithTooltip
              link={EXTERNAL_URLS.shoppingList}
              title={t('reports.shoppingList.tooltip')}
            />
            {reportKey && (
              <ReportConfigurator
                companyId={companyId}
                brandId={brandId}
                report={reportKey}
                fields={fields}
              />
            )}
          </div>
        }
        className={classes.cardWrapper}
      >
        <Dialog open={isGenerating || isGeneratingPreview}>
          <DialogContent>
            <div className={classes.dialogContent}>
              <h1>
                {isGeneratingPreview
                  ? t('reports.generatePreview.loader')
                  : t('reports.generate')}
              </h1>
              <CircularProgress />
            </div>
          </DialogContent>
        </Dialog>
        <CardBody>
          {parameters.map((el, index) => (
            <IndividualMultiplierReportRow
              errors={errors[index]}
              key={el.key}
              rowKey={el.key}
              onRowRemoved={onRowRemoved}
              onRowUpdated={onRowUpdated}
              canBeRemoved={index !== 0}
              brands={el.brands}
              subBrands={el.subBrands}
              zoneCategories={el.zoneCategories}
              recipeTags={el.recipeTags}
              dateFrom={el.dateFrom}
              dateTo={el.dateTo}
              multiplier={el.multiplier}
              includeSubscriptions={el.includeSubscriptions}
              useDateRange={useDateRange}
              useSubBrands={useSubBrands}
              useZoneCategories={useZoneCategories}
              useRecipeTags={useRecipeTags && !singleSelectRecipeTags}
              showMultiplier={showMultiplier}
              rowIndex={index}
              multiplierMatrixData={multiplierMatrixDataRows[el.key] || []}
              setMultiplierMatrixData={setMultiplierMatrixDataRows}
              isIndividualMultipliersEnabled={el.isIndividualMultipliersEnabled}
              setParameters={setParameters}
            />
          ))}
          <GridContainer justify="flex-end">
            {singleSelectWarehouse && (
              <GridItem sm={2}>
                <FormLabel
                  className={classNames(
                    classes.labelHorizontal,
                    classes.formLabel
                  )}
                >
                  {t('reports.selectWarehouse')}
                </FormLabel>
                <SelectInput
                  noGrid={true}
                  classes={classes}
                  mapBy="name"
                  trackBy="id"
                  name="warehouse"
                  value={warehouse}
                  options={availableWarehouses}
                  handleChange={handleWarehouse}
                />
              </GridItem>
            )}
            {singleSelectRecipeTags && (
              <GridItem sm={isTagSelectV2Enabled ? 4 : 2}>
                <FormLabel
                  className={classNames(
                    classes.labelHorizontal,
                    classes.formLabel
                  )}
                >
                  {labelSelectTags || t('reports.selectRecipeTags')}
                </FormLabel>
                <Tooltip
                  className={classes.tooltip}
                  title={
                    // eslint-disable-next-line no-nested-ternary
                    [
                      'SHOPPING_LIST',
                      'CHECK_LIST_RECIPES',
                      'RECIPES_CARD',
                    ].some(el => el === name)
                      ? t('reports.selectTags.recipe.tooltip')
                      : ['CHECK_LIST_DISHES', 'DISHES_CARD'].some(
                          el => el === name
                        )
                      ? t('reports.selectTags.dish.tooltip')
                      : ''
                  }
                  placement="right"
                >
                  <Info fontSize="small" />
                </Tooltip>
                {isTagSelectV2Enabled ? (
                  <ProductionTagSelect
                    selectedTags={recipeTagsV2}
                    selectedMode={recipeTagsMode}
                    onChange={handleUpdateRecipeTagsV2AndMode}
                  />
                ) : (
                  <SelectAll
                    className="input-select--production-tags"
                    options={recipeTagsOptions}
                    trackBy="@id"
                    mapBy="value"
                    optionSelected={recipeTags}
                    handleChange={handleRecipeTags}
                  />
                )}
              </GridItem>
            )}
          </GridContainer>
          {children}
          <GridContainer justify="space-between">
            <GridItem>
              <Button
                onClick={addNextRow}
                disabled={isGenerating}
                color="default"
                round={true}
                justIcon={true}
              >
                <AddCircle />
              </Button>
            </GridItem>
            <div className={classes.submitButtons}>
              {(customActions ?? []).map(({ onClick, label }) => (
                <GridItem key={label}>
                  <Button
                    onClick={async () => {
                      setIsGenerating(true);
                      try {
                        const params = getParams();
                        await onClick(params);
                        setIsGenerating(false);
                      } catch {
                        setIsGenerating(false);
                      }
                    }}
                    disabled={isGenerating}
                    color="success"
                    round={true}
                  >
                    {label}
                  </Button>
                </GridItem>
              ))}
              <GridItem id={DOWNLOAD_REPORT_BUTTON_ID}>
                <DownloadReportButtons
                  isSalesMarketLangViewEnabled={isSalesMarketLangViewEnabled}
                  previewXlsxAvailable={previewXlsxAvailable}
                  mimeTypes={mimeTypes}
                  handlePreviewGenerate={handlePreviewGenerate}
                  handleGenerate={handleGenerate}
                  areButtonsDisabled={isGenerating}
                />
              </GridItem>
            </div>
          </GridContainer>
        </CardBody>
      </CardWrapper>
      <Card>
        <CardBody>
          <Archive
            options={{
              brands,
              subBrands: subBrandsOptions,
              zoneCategories,
              recipeTags: recipeTagsOptions,
            }}
            type="REPORT"
            reportName={archiveReportNames}
          />
        </CardBody>
      </Card>
      <ReportPreview
        excelContent={excelContent}
        isOpen={isReportPreviewOpen}
        parameters={parameters}
        recipeTags={
          isTagSelectV2Enabled
            ? recipeTagsV2.map(iri => {
                const tagOption = recipeTagsOptions.find(
                  opt => opt['@id'] === iri
                );
                return { value: iri, label: tagOption?.value ?? iri };
              })
            : recipeTags
        }
        recipeTagsMode={isTagSelectV2Enabled ? recipeTagsMode : undefined}
        showMultiplier={showMultiplier}
        title={title}
        useSubBrands={useSubBrands}
        useZoneCategories={useZoneCategories}
        downloadXls={() => {
          handleGenerate(MIME_TYPES.xlsx, DEFAULT_SALES_MARKET_LANG);
        }}
        onClose={() => {
          setIsReportPreviewOpen(false);
        }}
      />
    </>
  ) : (
    <AccessDenied />
  );
};

IndividualMultiplierReport.propTypes = {
  brands: PropTypes.array,
  zoneCategories: PropTypes.array,
  classes: PropTypes.any,
  archiveReportNames: PropTypes.arrayOf(PropTypes.string),
  fileName: PropTypes.func.isRequired,
  showMultiplier: PropTypes.bool,
  validateBrands: PropTypes.bool,
  usePost: PropTypes.bool,
  useDateRange: PropTypes.bool,
  useSubBrands: PropTypes.bool,
  useZoneCategories: PropTypes.bool,
  useRecipeTags: PropTypes.bool,
  mimeTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string,
  url: PropTypes.string.isRequired,
  isSalesMarketLangViewEnabled: PropTypes.bool,
  isTagSelectV2Enabled: PropTypes.bool,
};

export default IndividualMultiplierReport;
